import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTheme, useMediaQuery, Typography, Box } from '@mui/material';
import { AppPaper } from '../../layout/appPaper';
import { AutocompleteOption } from '../../models/autocompleteOption';
import { Task } from '../../models/task';
import { FormProvider, useForm } from 'react-hook-form';
import { AppControlledAutocomplete } from '../app/input/appControlledAutocomplete';
import {
  getEmployeeProjects,
} from '../../services/api/employeeServices';
import { NO_PROJECTS, NO_TASKS } from '../../constants';
import { ProfilePreferencesField } from '../../models/preferences';
import { generateProjectLabel } from '../../services/projectService';
import { useEmployeeProjectList } from '../../hooks/useEmployeeProjectList';
import { useGetEmployeeTasksQuery } from '../../store/tasks/tasksService';

interface Props {
  employeeId: string;
  preferredProjectId: string;
  preferredTaskId: string;
  onFieldChange: (
    field: ProfilePreferencesField,
    value: AutocompleteOption | Task | null
  ) => void;
}

export function PreferredProjectTaskSetting({
  employeeId,
  preferredProjectId,
  preferredTaskId,
  onFieldChange,
}: Props): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [projectsFilterQuery, setProjectsFilterQuery] = useState<string>('');
  const [tasksFilterQuery, setTasksFilterQuery] = useState<string>('');

  const methods = useForm();

  const [watchProject] = methods.watch([ProfilePreferencesField.PROJECT]);

  const { projects, isLoading: isLoadingProjects } = useEmployeeProjectList(
    employeeId || '',
    projectsFilterQuery
  );
  const { data: tasksData, isLoading: isLoadingTasks } = useGetEmployeeTasksQuery(
    {
      internalId: employeeId || '',
      projectId: watchProject?.id || '',
      filter: tasksFilterQuery,
    },
    {
      skip: !employeeId || !watchProject?.id,
    }
  );
  const tasks = useMemo(() => tasksData?.items || [], [tasksData]);

  const {
    setValue: setFormValue,
    formState: { errors },
  } = methods;

  const onSearchProjects = useCallback(
    (name: string) => setProjectsFilterQuery(name),
    []
  );

  const onSearchTasks = useCallback(
    (name: string) => setTasksFilterQuery(name),
    []
  );

  const onSelectProject = useCallback(() => {
    setFormValue('task', null);
    setProjectsFilterQuery('');
    setTasksFilterQuery('');
  }, [setFormValue, setProjectsFilterQuery, setTasksFilterQuery]);

  const onSelectTask = useCallback(() => {
    setTasksFilterQuery('');
  }, [setTasksFilterQuery]);

  const handleChange = useCallback(
    (field: ProfilePreferencesField, value: AutocompleteOption | Task | null) => {
      onFieldChange(field, value);
    },
    [onFieldChange]
  );

  useEffect(() => {
    if (preferredProjectId && projects.length > 0) {
      const projectOption = projects.find((p) => p.id === preferredProjectId) || null;
      setFormValue(ProfilePreferencesField.PROJECT, projectOption);
    }
  }, [preferredProjectId, projects, tasks, setFormValue]);

  useEffect(() => {
    if (preferredTaskId && tasks.length > 0) {
      const taskOption = tasks.find((t) => t.id === preferredTaskId) || null;
      setFormValue(ProfilePreferencesField.TASK, taskOption);
    }
  }, [preferredTaskId, projects, tasks, setFormValue]);

  return (
    <AppPaper isMobile={isMobile} sx={{ height: '100%' }}>
      <Typography sx={{ mt: 3, mb: 3 }} variant="h4">
        Preferred Project and Task
      </Typography>
      <FormProvider {...methods}>
        <form>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <AppControlledAutocomplete
              name={ProfilePreferencesField.PROJECT}
              label="Project"
              options={projects}
              searchOptions={onSearchProjects}
              onSelect={onSelectProject}
              sx={{ width: 300 }}
              isError={!!errors[ProfilePreferencesField.PROJECT]}
              isLoading={isLoadingProjects}
              noOptionsText={NO_PROJECTS}
              handleChange={(_, data) => {
                handleChange(ProfilePreferencesField.PROJECT, data);
              }}
            />
            <AppControlledAutocomplete
              name={ProfilePreferencesField.TASK}
              label="Task"
              options={tasks}
              getOptionLabel={(task) => (typeof task === 'string' ? task : task.name)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              searchOptions={onSearchTasks}
              onSelect={onSelectTask}
              sx={{ width: 300 }}
              isError={!!errors[ProfilePreferencesField.TASK]}
              disabled={!watchProject}
              isLoading={isLoadingTasks}
              noOptionsText={NO_TASKS}
              handleChange={(_, data) => {
                handleChange(ProfilePreferencesField.TASK, data);
              }}
            />
          </Box>
        </form>
      </FormProvider>
    </AppPaper>
  );
}
